import { type ConversationQuota, effectiveConversationLimit, isOverConversationLimit } from "./conversation-limit.ts";
import { isRecargasPlan, RECARGAS_PURCHASE_REFUSAL } from "./credits-plan.ts";

export const DEFAULT_EXTRA_AGENT_PRICE_COP = 40000;
export const DEFAULT_PER_USE_ACTIVATION_FEE_COP = 2000;
const ANNUAL_PRICE_FACTOR = 0.8;
const MONTHS_PER_YEAR = 12;

export const CHECKOUT_MODES = new Set([
  "one_shot",
  "annual",
  "extra_agent",
  "extra_pack",
  "overage",
  "per_use_cycle",
  "per_use_activation",
]);

interface PricedPlan {
  price_monthly?: number | string | null;
  conversations_included?: number | null;
  billing_mode?: string | null;
  per_conversation_price?: number | string | null;
  extra_agent_price?: number | string | null;
  name?: string | null;
}

function positive(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function annualPlanAmount(priceMonthly: unknown): number {
  const price = positive(priceMonthly);
  return price > 0 ? Math.ceil(price * ANNUAL_PRICE_FACTOR) * MONTHS_PER_YEAR : 0;
}

export function planPurchaseAmount(mode: string, priceMonthly: unknown): number {
  return mode === "annual" ? annualPlanAmount(priceMonthly) : positive(priceMonthly);
}

export function extraAgentUnitPrice(planExtraAgentPrice: unknown): number {
  return positive(planExtraAgentPrice) || DEFAULT_EXTRA_AGENT_PRICE_COP;
}

export function perConversationPrice(plan: PricedPlan | null | undefined): number {
  if (!plan) return 0;
  const included = Number(plan.conversations_included ?? 0);
  const monthly = positive(plan.price_monthly);
  if (plan.billing_mode !== "per_use" && included > 0 && monthly > 0) {
    return Math.round(monthly / included);
  }
  return positive(plan.per_conversation_price);
}

export function overageCharge(
  org: ConversationQuota | null | undefined,
  plan: PricedPlan | null | undefined,
): { conversations: number; amount: number } {
  if (!org || !isOverConversationLimit(org)) return { conversations: 0, amount: 0 };
  const conversations = Math.max(0, Number(org.conversations_used ?? 0) - (effectiveConversationLimit(org) ?? 0));
  return { conversations, amount: Math.ceil(perConversationPrice(plan) * conversations) };
}

// deno-lint-ignore no-explicit-any
type Db = any;

async function loadPlan(supabase: Db, planId: unknown, columns: string): Promise<PricedPlan | null> {
  if (!planId) return null;
  const { data } = await supabase.from("plans").select(columns).eq("id", String(planId)).maybeSingle();
  return (data as PricedPlan | null) ?? null;
}

export async function extraAgentAmount(
  supabase: Db,
  organizationId: string,
): Promise<{ amount: number; planName: string | null }> {
  const { data: org } = await supabase.from("organizations").select("plan").eq("id", organizationId).maybeSingle();
  const plan = await loadPlan(supabase, org?.plan, "extra_agent_price, name");
  return { amount: extraAgentUnitPrice(plan?.extra_agent_price), planName: plan?.name ?? null };
}

export async function overageAmount(
  supabase: Db,
  organizationId: string,
): Promise<{ amount: number; conversations: number }> {
  const { data: org } = await supabase
    .from("organizations")
    .select("plan, conversations_used, conversations_limit, conversations_extra, overage_accepted_at")
    .eq("id", organizationId)
    .maybeSingle();
  const plan = await loadPlan(
    supabase,
    org?.plan,
    "price_monthly, conversations_included, billing_mode, per_conversation_price",
  );
  return overageCharge(org, plan);
}

export async function perUseActivationFee(supabase: Db): Promise<number> {
  const { data } = await supabase
    .from("platform_settings")
    .select("value")
    .eq("key", "per_use_activation_fee")
    .eq("brand", "flow")
    .maybeSingle();
  return positive(data?.value) || DEFAULT_PER_USE_ACTIVATION_FEE_COP;
}

export const UNPRICED_LEGACY_MODES = new Set([
  "overage",
  "card_setup",
  "extra_agent_monthly",
  "per_use_cycle",
  "direct_payment",
]);

export async function legacyCheckoutExpectedAmount(
  supabase: Db,
  ref: Record<string, unknown>,
): Promise<number | null | "unpriced"> {
  const mode = String(ref.mode || "one_shot");
  if (UNPRICED_LEGACY_MODES.has(mode)) return "unpriced";

  if (mode === "extra_pack") {
    if (!ref.extra_pack_id) return null;
    const { data: pack } = await supabase
      .from("extra_pack_catalog")
      .select("price_cop")
      .eq("id", String(ref.extra_pack_id))
      .maybeSingle();
    return positive(pack?.price_cop) || null;
  }

  if (mode === "extra_agent" || ref.extra_agent === true) {
    const plan = await loadPlan(supabase, ref.plan_id, "extra_agent_price");
    return extraAgentUnitPrice(plan?.extra_agent_price);
  }

  if (mode === "per_use_activation") return perUseActivationFee(supabase);

  const plan = await loadPlan(supabase, ref.plan_id, "price_monthly");
  return planPurchaseAmount(ref.billing === "annual" || mode === "annual" ? "annual" : "one_shot", plan?.price_monthly) ||
    null;
}

export const PLAN_PURCHASE_MODES = new Set(["one_shot", "annual"]);

interface PurchasablePlan {
  id: string;
  is_custom?: boolean | null;
  billing_mode?: string | null;
}

export function planPurchaseRefusal(
  plan: PurchasablePlan | null | undefined,
  organizationPlan: string | null | undefined,
): string | null {
  if (!plan) return "El plan elegido no existe.";
  if (isRecargasPlan(plan.id)) return RECARGAS_PURCHASE_REFUSAL;
  if (plan.billing_mode === "per_use") return "Los planes de pago por uso no se compran desde aquí.";
  if (plan.is_custom && plan.id !== organizationPlan) return "Este plan no está disponible para tu cuenta.";
  return null;
}

export async function planPurchaseEligibility(
  supabase: Db,
  planId: string | null | undefined,
  organizationId: string,
): Promise<string | null> {
  if (!planId) return "Elige un plan para continuar.";
  const [{ data: plan }, { data: org }] = await Promise.all([
    supabase.from("plans").select("id, is_custom, billing_mode").eq("id", String(planId)).maybeSingle(),
    supabase.from("organizations").select("plan").eq("id", organizationId).maybeSingle(),
  ]);
  return planPurchaseRefusal(plan, org?.plan ?? null);
}

export function cardCapturePlanRefusal(
  plan: PurchasablePlan | null | undefined,
  organizationPlan: string | null | undefined,
): string | null {
  if (!plan) return "El plan elegido no existe.";
  if (plan.id !== organizationPlan) return "Este plan no está disponible para tu cuenta.";
  return null;
}

export interface CreditTopupLimits {
  minCop: number;
  maxCop: number;
  presetsCop: number[];
}

export const CREDIT_TOPUP_CAP_COP = 10_000_000;
export const CREDIT_TOPUP_MAX_PRESETS = 8;

export const DEFAULT_CREDIT_TOPUP_LIMITS: CreditTopupLimits = Object.freeze({
  minCop: 50_000,
  maxCop: CREDIT_TOPUP_CAP_COP,
  presetsCop: Object.freeze([50_000, 100_000, 200_000, 500_000, 1_000_000]) as number[],
});

function positiveInteger(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isSafeInteger(n) && n > 0 ? n : null;
}

export function parseCreditTopupLimits(raw: unknown): CreditTopupLimits {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return DEFAULT_CREDIT_TOPUP_LIMITS;
  const row = raw as Record<string, unknown>;
  const minCop = positiveInteger(row.min_cop);
  const maxCop = positiveInteger(row.max_cop);
  if (minCop === null || maxCop === null || maxCop < minCop || maxCop > CREDIT_TOPUP_CAP_COP) return DEFAULT_CREDIT_TOPUP_LIMITS;
  const presets = Array.isArray(row.presets_cop) ? row.presets_cop.map(positiveInteger) : [];
  const presetsCop = [...new Set(presets.filter((p): p is number => p !== null && p >= minCop && p <= maxCop))]
    .sort((a, b) => a - b)
    .slice(0, CREDIT_TOPUP_MAX_PRESETS);
  return { minCop, maxCop, presetsCop };
}

export type CreditTopupLimitsCheck = { ok: true; value: CreditTopupLimits } | { ok: false; error: string };

export function checkCreditTopupLimits(input: { minCop: unknown; maxCop: unknown; presetsCop: unknown }): CreditTopupLimitsCheck {
  const minCop = positiveInteger(input.minCop);
  if (minCop === null) return { ok: false, error: "El mínimo debe ser un valor entero en pesos, mayor que 0." };
  const maxCop = positiveInteger(input.maxCop);
  if (maxCop === null) return { ok: false, error: "El máximo debe ser un valor entero en pesos, mayor que 0." };
  if (maxCop < minCop) return { ok: false, error: "El máximo no puede ser menor que el mínimo." };
  if (maxCop > CREDIT_TOPUP_CAP_COP) return { ok: false, error: `El máximo no puede pasar de ${pesos(CREDIT_TOPUP_CAP_COP)}.` };
  if (!Array.isArray(input.presetsCop)) return { ok: false, error: "Escribe al menos un monto sugerido." };
  const presets: number[] = [];
  for (const raw of input.presetsCop) {
    const value = positiveInteger(raw);
    if (value === null) return { ok: false, error: "Cada monto sugerido debe ser un valor entero en pesos." };
    if (value < minCop || value > maxCop) {
      return { ok: false, error: `Cada monto sugerido debe estar entre ${pesos(minCop)} y ${pesos(maxCop)}.` };
    }
    presets.push(value);
  }
  const presetsCop = [...new Set(presets)].sort((a, b) => a - b);
  if (presetsCop.length === 0) return { ok: false, error: "Escribe al menos un monto sugerido." };
  if (presetsCop.length > CREDIT_TOPUP_MAX_PRESETS) {
    return { ok: false, error: `Máximo ${CREDIT_TOPUP_MAX_PRESETS} montos sugeridos.` };
  }
  return { ok: true, value: { minCop, maxCop, presetsCop } };
}

// deno-lint-ignore no-explicit-any
export async function readCreditTopupLimits(supabase: any): Promise<CreditTopupLimits> {
  try {
    const { data, error } = await supabase.rpc("credit_topup_settings_current");
    if (error) {
      console.warn("[checkout-pricing] credit_topup_settings_current failed, using defaults:", error.message);
      return DEFAULT_CREDIT_TOPUP_LIMITS;
    }
    return parseCreditTopupLimits(data);
  } catch (e) {
    console.warn("[checkout-pricing] credit_topup_settings_current threw, using defaults:", (e as Error)?.message);
    return DEFAULT_CREDIT_TOPUP_LIMITS;
  }
}

export function voiceTopupLimits(limits: CreditTopupLimits, voiceMinCop: number | null): CreditTopupLimits {
  const floor = voiceMinCop !== null && Number.isInteger(voiceMinCop) && voiceMinCop > 0 ? voiceMinCop : 0;
  const minCop = Math.min(Math.max(limits.minCop, floor), limits.maxCop);
  const presets = limits.presetsCop.filter((p) => p >= minCop && p <= limits.maxCop);
  return { minCop, maxCop: limits.maxCop, presetsCop: presets.length > 0 ? presets : [minCop] };
}

// deno-lint-ignore no-explicit-any
export async function readVoiceTopupMinCop(supabase: any): Promise<number | null> {
  const { data, error } = await supabase.rpc("voice_topup_min_cop");
  if (error) throw new Error(`voice_topup_min_cop failed: ${error.message}`);
  const value = Number(data);
  return data !== null && Number.isInteger(value) && value > 0 ? value : null;
}

function pesos(amount: number): string {
  return `$${amount.toLocaleString("es-CO")}`;
}

export const CREDIT_TOPUP_ETB_REFUSAL = "Las recargas de saldo no están disponibles para cuentas ETB.";

export function creditTopupBrandRefusal(brand: unknown): string | null {
  return brand === "etb" ? CREDIT_TOPUP_ETB_REFUSAL : null;
}

export function creditTopupRefusal(amount: unknown, limits: CreditTopupLimits = DEFAULT_CREDIT_TOPUP_LIMITS): string | null {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "Escribe el monto a recargar.";
  if (!Number.isInteger(amount)) return "El monto debe ser en pesos enteros.";
  if (amount < limits.minCop) return `La recarga mínima es de ${pesos(limits.minCop)}.`;
  if (amount > limits.maxCop) return `La recarga máxima es de ${pesos(limits.maxCop)}.`;
  return null;
}
